import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COMPANY } from "@/lib/site";
import { ArrowUpRight, MapPin, Phone, Mail, Clock, Building2 } from "lucide-react";

export default function KontaktPage() {
  const { t } = useTranslation();
  const DETAILS = [
    { key: "address", icon: MapPin, label: t("kontakt.labels.address"), value: COMPANY.address, href: "https://www.google.com/maps?q=Visoka+59,+21000+Split" },
    { key: "phone", icon: Phone, label: t("kontakt.labels.phone"), value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s/g, "")}` },
    { key: "email", icon: Mail, label: t("kontakt.labels.email"), value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { key: "hours", icon: Clock, label: t("kontakt.labels.hours"), value: COMPANY.hours },
    { key: "oib", icon: Building2, label: t("kontakt.labels.oib"), value: COMPANY.oib },
  ];

  return (
    <div data-testid="kontakt-page">
      <Seo title={t("kontakt.seoTitle")} path="/kontakt" description={t("kontakt.seoDesc")} />
      {/* HERO */}
      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink-soft/40" /> {t("kontakt.kicker")}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
                {t("kontakt.heroTitle1")}<br />
                <span className="text-ink/40">{t("kontakt.heroTitle2")}</span>
              </h1>
            </div>
            <div className="md:col-span-4 text-ink-soft leading-relaxed">
              {t("kontakt.heroLead", { company: COMPANY.name })}
            </div>
          </div>
        </div>
      </section>

      {/* DETAILS + MAP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12">
          {/* INFO */}
          <div className="md:col-span-5">
            <div className="overline text-ink-soft">{t("kontakt.detailsKicker")}</div>
            <div className="mt-6 border border-hair divide-y divide-hair bg-white">
              {DETAILS.map((d) => {
                const Icon = d.icon;
                const content = (
                  <div className="flex items-start gap-4 p-5">
                    <div className="w-10 h-10 bg-ink text-white flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="overline text-ink-soft">{d.label}</div>
                      <div className="font-display text-lg text-ink mt-1 break-words">{d.value}</div>
                    </div>
                  </div>
                );
                return d.href ? (
                  <a key={d.key} href={d.href} target={d.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="block hover:bg-[#FBF7F2] transition-colors" data-testid={`kontakt-${d.key}`}>
                    {content}
                  </a>
                ) : (
                  <div key={d.key}>{content}</div>
                );
              })}
            </div>

            <Link
              to="/prijava"
              data-testid="kontakt-cta"
              className="inline-flex items-center gap-2 mt-8 bg-[#D92D20] text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#B91C1C] transition-colors"
            >
              {t("kontakt.formCta")}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </div>

          {/* MAP */}
          <div className="md:col-span-7">
            <div className="overline text-ink-soft">{t("kontakt.mapKicker")}</div>
            <div className="mt-6 border border-hair overflow-hidden aspect-[4/3] md:aspect-auto md:h-[520px]">
              <iframe
                title="Lokacija ureda Infokarta"
                src="https://www.google.com/maps?q=Visoka+59,+21000+Split,+Croatia&output=embed"
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="kontakt-map"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-hair bg-ink text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-white/60">{t("kontakt.ctaKicker")}</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-semibold mt-3 leading-tight">
              {t("kontakt.ctaTitle")}
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-2 bg-white text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-white/90 transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={1.75} />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
